import { useQuery } from '@tanstack/react-query';
import { bidKeys, type PendingFeedbackList } from '@tempmee/awards';
import { useAppSelector } from '~/hooks';
import { getPendingBidFeedbackService } from '../services/GetPendingBidFeedback';

/**
 * Pending-feedback list for the Today's-Shift tab (`GET /feedback/pending`,
 * awards-service#37): one row per (office, professional) pair the caller
 * still owes feedback on. `totalCount` drives the tab badge; an empty
 * `cycleId` means no cycle is accepting applications and the tab must not
 * render (rows are empty in that case too).
 *
 * @param enabled - allows callers (e.g. the Today's-Shift tab, gated behind
 * the v2 flag) to skip the fetch entirely instead of firing it and
 * discarding the result — matches `useBidHistoryQuery`'s `enabled` pattern.
 */
export const usePendingBidFeedback = (enabled = true) => {
  // Office-scoped like useBidHistoryQuery: the response depends on the
  // x-office header, so a DSO admin switching child offices must miss the
  // previous office's cache entry (GOTU-4820 parent/child rule).
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  return useQuery<PendingFeedbackList>({
    queryKey: [...bidKeys.pendingFeedback(), selectedOfficeId],
    queryFn: () => getPendingBidFeedbackService(),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  });
};
